import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePatientsStore } from "@/stores/patientsStore";
import { useToast } from "@/components/ui/use-toast";
import { api } from "@/lib/api";

interface BulkLanguageUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientIds: string[];
}

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "vi", label: "Vietnamese" },
  { value: "zh", label: "Chinese" },
  { value: "ko", label: "Korean" },
  { value: "tl", label: "Tagalog" },
  { value: "ar", label: "Arabic" },
  { value: "fr", label: "French" },
  { value: "pt", label: "Portuguese" },
  { value: "ru", label: "Russian" },
  { value: "hi", label: "Hindi" },
  { value: "ja", label: "Japanese" },
];

const BulkLanguageUpdateModal = ({ isOpen, onClose, patientIds }: BulkLanguageUpdateModalProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { updatePatientsAfterBulkLanguageChange } = usePatientsStore();
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!selectedLanguage) {
      toast({
        title: "No language selected",
        description: "Please select a language to continue.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      await api.updateMultiplePatientsLanguage(patientIds, selectedLanguage);
      updatePatientsAfterBulkLanguageChange(patientIds, selectedLanguage);
      toast({
        title: "Language updated successfully",
        description: `Updated language to ${LANGUAGE_OPTIONS.find(opt => opt.value === selectedLanguage)?.label} for ${patientIds.length} patient(s).`,
      });
      onClose();
      setSelectedLanguage("");
    } catch (error) {
      toast({
        title: "Failed to update language",
        description: "An error occurred while updating the language for patients.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedLanguage("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Update Language for Selected Patients</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="language-select" className="text-sm font-medium">
              Select Language
            </label>
            <Select
              value={selectedLanguage}
              onValueChange={setSelectedLanguage}
            >
              <SelectTrigger id="language-select">
                <SelectValue placeholder="Choose a language" />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="text-sm text-muted-foreground">
            {patientIds.length} patient{patientIds.length !== 1 ? "s" : ""}{" "}
            selected
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedLanguage || isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Update Language"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkLanguageUpdateModal;